import React, { ReactNode, useEffect, useState } from 'react';

import StepperProgress from './StepperProgress';
import { InjectedStepProps, StepProps } from './Step';

interface StepperProps {
  activeStep: number;
  children: ReactNode;
}

const Stepper = ({ activeStep, children }: StepperProps) => {
  const stepsCount = React.Children.count(children);

  const getTranslation = (currentStep: number, totalSteps: number): number => {
    // If something is wrong (e.g. missing steps)
    if (totalSteps <= 0 || currentStep <= 0) {
      return 0;
    }

    return -((currentStep - 1) * (100 / totalSteps));
  };

  const [translation, setTranslation] = useState(() => getTranslation(activeStep, stepsCount));

  const propsInjectedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      const step = index + 1;
      return React.cloneElement(child as React.ReactElement<{ inject: InjectedStepProps }>, {
        inject: {
          isActiveStep: activeStep === step,
        },
      });
    }
  });

  const stepIcons =
    React.Children.toArray(children).map(child => {
      if (React.isValidElement(child)) {
        const stepProps = child.props as StepProps;
        return stepProps.stepIcon ?? null;
      }
      return null;
    }) || [];

  useEffect(() => {
    // Define translation
    const computedTranslation = getTranslation(activeStep, stepsCount);
    setTranslation(computedTranslation);
  }, [activeStep]);

  return (
    <div className="w-full overflow-x-clip flex flex-col">
      <StepperProgress count={stepsCount} activeStep={activeStep} stepIcons={stepIcons} />
      <div
        style={{
          width: `${stepsCount * 100}%`,
          transform: `translateX(${translation}%)`,
        }}
        className="flex transition-transform duration-150 ease-out"
      >
        {propsInjectedChildren}
      </div>
    </div>
  );
};

export default Stepper;
